import { HAS_SNOOZED_INSTALL_PROMPT, HAS_SNOOZED_INSTALL_PROMPT_COUNT } from 'constants/messages';
import { HAS_INSTALLED_MEMORY_TRACKER } from 'constants/user';
import { getTicViewDemoMode } from 'redux/modules/tic/tic.demo.selectors';
import { getIsMemoryAppConnected } from 'redux/selectors/apps';
import { getHasRecommendedMemoryTracker } from 'redux/selectors/memoryTracker';
import { getIsRouteCalendar } from 'redux/selectors/routing';
import { getUser, getUserProperties } from 'redux/selectors/user';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import { timestamp } from 'utils';
import { getForceShowInstallMemoryMessage } from '../development';

/** Install Banner:
 * - Show if the user is new: less than 8 days ago
 * - Show blue banner immediately once user is done with the sign-up process and haven’t installed Memory
 * - If user has clicked on ‘Remind me later’ agaib 30 days and if they still haven’t installed Memory.
 */

const SNOOZE_DURATION_DAYS = 30;
const MAX_SNOOZES = 3;
const NEW_USER_THRESHOLD_DAYS = 9;
type UnixTime = number;
const readIsBannerClosed = (state: IState): boolean => {
  return state.messages.closed;
};

export const getIsBannerClosed = readIsBannerClosed;

const getDidUserSignUpRecently = createSelector([getUser], (user): boolean => {
  const createdAt = user.created_at;
  const daysAgo = Math.floor((timestamp() - createdAt) / (24 * 60 * 60));
  return daysAgo < NEW_USER_THRESHOLD_DAYS;
});

const getDidInstallTracker = createSelector(
  [getUserProperties, getIsMemoryAppConnected],
  (userProperties, isMemoryAppConnected): boolean => {
    return !!userProperties[HAS_INSTALLED_MEMORY_TRACKER] || isMemoryAppConnected;
  }
);

const getDidUserDismissBanner = createSelector(
  [getUserProperties],
  (userProperties): boolean => !!userProperties[HAS_SNOOZED_INSTALL_PROMPT]
);

const getLastSnooze = createSelector([getUserProperties], (userProperties): UnixTime => {
  const snoozeDate = userProperties[HAS_SNOOZED_INSTALL_PROMPT];
  return parseInt(String(snoozeDate), 10);
});

export const getSnoozeCount = createSelector([getUserProperties], (userProperties): number => {
  const snoozeCount = userProperties[HAS_SNOOZED_INSTALL_PROMPT_COUNT];
  return parseInt(String(snoozeCount), 10) || 0;
});

const getDidExceedSnoozeCount = createSelector([getSnoozeCount], (snoozeCount): boolean => {
  return snoozeCount > MAX_SNOOZES;
});

const getIsTimeToShowBannerAgain = createSelector([getLastSnooze], (lastSnooze): boolean => {
  const daysAgo = Math.floor((timestamp() - lastSnooze) / (24 * 60 * 60));
  return daysAgo > SNOOZE_DURATION_DAYS;
});

export const getShowInstallBanner = createSelector(
  [
    getHasRecommendedMemoryTracker,
    getIsRouteCalendar,
    getDidUserDismissBanner,
    getIsTimeToShowBannerAgain,
    getDidInstallTracker,
    getDidExceedSnoozeCount,
    getDidUserSignUpRecently,
    getTicViewDemoMode,
    getForceShowInstallMemoryMessage,
  ],
  (
    supportsTracker,
    isRouteCalendar,
    didUserDismissBanner,
    isTimeToShowBannerAgain,
    didInstallTracker,
    didExceedSnoozeCount,
    didUserSignUpRecently,
    ticViewDemoMode,
    forceShowInstallMemoryMessageDevtoolsOverride
  ): boolean => {
    // We only have trackers for certain platforms
    if (!supportsTracker) {
      return false;
    }

    // Only show on calendar routes
    if (!isRouteCalendar) {
      return false;
    }

    if (ticViewDemoMode.enabled) {
      // Don't show if demo mode is enabled
      return false;
    }

    if (forceShowInstallMemoryMessageDevtoolsOverride) {
      // Before any checks we don't care about, implement the devtools override
      return true;
    }

    // Only show to users who have not installed the tracker
    if (didInstallTracker) {
      return false;
    }

    // Hide from users who have seen it too many times...
    if (didExceedSnoozeCount) {
      return false;
    }

    // Show if they dismissed it, but it's been a while.
    if (didUserDismissBanner && isTimeToShowBannerAgain) {
      return true;
    }

    // Only show a fresh banner to users who have signed up recently
    if (!didUserSignUpRecently) {
      return false;
    }

    // and of course don't show it if they dismissed it.
    return !didUserDismissBanner;
  }
);
